/**
 * «¿Renovó o ya se fue?», en el alta de inquilino, debajo de las fechas de su habitación.
 *
 * Se sueltan sus hojas de RENOVACIÓN y de RESCISIÓN y el servidor dice qué es cada una y sus
 * fechas; quien decide qué hacer con ellas es el panel de alta. Sirven para dar de alta a alguien
 * con su historia entera: con la rescisión, su estancia acaba el día que se fue y no choca con
 * quien entró después; las renovaciones alargan el contrato como en su ficha.
 *
 * LAS HOJAS SE GUARDAN AL CREAR, NO AL LEERLAS, como el contrato: leer no deja nada en el
 * servidor. Al crear, el panel las sube a la ficha —renovaciones por orden y la rescisión la
 * última—, que es donde se encadenan y se aplican.
 */
import { useRef, useState } from 'react';
import type { AgreementReadingResponse } from '@naar/shared';
import { ApiError, api } from '../../api/client';
import { Icon } from '../../components/Icon';
import { toast } from '../../components/feedback/Toaster';
import { formatEur, formatShortDate } from '../../lib/format';
import styles from './ContractReader.module.css';

export interface HojaLeida {
  fichero: File;
  lectura: AgreementReadingResponse;
}

interface AgreementSheetsProps {
  hojas: HojaLeida[];
  onChange: (hojas: HojaLeida[]) => void;
}

/** «Renovación · 01/08/26 – 30/06/27 · 397 €» o «Rescisión · se fue el 28/02/26». */
export function describirHoja({ lectura }: HojaLeida): string {
  if (lectura.kind === 'rescision') {
    return lectura.exitDate ? `Rescisión · se fue el ${formatShortDate(lectura.exitDate)}` : 'Rescisión sin fecha';
  }
  const r = lectura.renewal;
  const fechas = [r?.startDate && formatShortDate(r.startDate), r?.endDate && formatShortDate(r.endDate)]
    .filter(Boolean)
    .join(' – ');
  return ['Renovación', fechas, r?.monthlyRent && formatEur(r.monthlyRent)].filter(Boolean).join(' · ');
}

export function AgreementSheets({ hojas, onChange }: AgreementSheetsProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [leyendo, setLeyendo] = useState(false);

  async function leer(files: FileList | null | undefined) {
    if (!files || files.length === 0) return;
    setLeyendo(true);
    const nuevas: HojaLeida[] = [];
    for (const fichero of Array.from(files)) {
      try {
        const lectura = await api.readTenantAgreement(fichero);
        // Solo las que son de verdad una renovación o una rescisión del modelo.
        if (!lectura.kind) {
          toast.error(`«${fichero.name}»: ${lectura.warnings[0] ?? 'no parece una hoja de renovación ni de rescisión.'}`);
          continue;
        }
        if (lectura.kind === 'rescision' && !lectura.exitDate) {
          toast.error(`«${fichero.name}»: no se encuentra la fecha de la rescisión.`);
          continue;
        }
        nuevas.push({ fichero, lectura });
      } catch (error) {
        toast.error(error instanceof ApiError ? error.message : `No se ha podido leer «${fichero.name}».`);
      }
    }
    setLeyendo(false);
    if (nuevas.length === 0) return;
    // Una sola rescisión: la última que se suelta sustituye a la anterior.
    const rescisiones = nuevas.filter((hoja) => hoja.lectura.kind === 'rescision');
    const ultima = rescisiones[rescisiones.length - 1];
    onChange([
      ...hojas.filter((hoja) => !(ultima && hoja.lectura.kind === 'rescision')),
      ...nuevas.filter((hoja) => hoja.lectura.kind !== 'rescision' || hoja === ultima),
    ]);
  }

  return (
    <div className={styles.reader}>
      <div
        className={over ? `${styles.drop} ${styles.dropOver}` : styles.drop}
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          void leer(event.dataTransfer.files);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          multiple
          className={styles.dropInput}
          onChange={(event) => {
            void leer(event.target.files);
            event.target.value = '';
          }}
        />
        <button
          type="button"
          className={styles.dropButton}
          onClick={() => inputRef.current?.click()}
          disabled={leyendo}
        >
          <Icon name="file" size={15} strokeWidth={2} />
          {leyendo ? 'Leyendo…' : '¿Renovó o ya se fue? Suelta aquí sus hojas de renovación o rescisión (PDF)'}
        </button>
      </div>

      {hojas.length > 0 && (
        <div className={styles.result} role="status">
          {hojas.map((hoja) => (
            <p key={`${hoja.fichero.name}-${hoja.fichero.size}`}>
              <Icon name="file-lines" size={14} /> {describirHoja(hoja)} — «{hoja.fichero.name}»{' '}
              <button
                type="button"
                className={styles.quitar}
                onClick={() => onChange(hojas.filter((otra) => otra !== hoja))}
                aria-label={`Quitar «${hoja.fichero.name}»`}
              >
                Quitar
              </button>
            </p>
          ))}
          <p className={styles.saved}>Al crearlo se guardan en su ficha y cambian su contrato como allí.</p>
        </div>
      )}

      {hojas.flatMap((hoja) => hoja.lectura.warnings).map((aviso) => (
        <div key={aviso} className={styles.warning}>
          <Icon name="info" size={15} />
          <span>{aviso}</span>
        </div>
      ))}
    </div>
  );
}
